import type { LibConfig } from '../types/lib-config'
import { initImpl, initShareButtonsImpl, destroyImpl, updateImpl } from '../browser/init-impl.umd'
import { ensureBootstrapIcons } from './ensureBootstrapIcons';

const isBrowser =
  typeof window !== 'undefined' && typeof document !== 'undefined'

const toSafeToken = (value: unknown): string => {
  const text = typeof value === 'string' ? value.trim() : ''
  if (!text) return ''
  return text.replace(/[^a-zA-Z0-9_-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
}

const withAutoInstanceIds = (configs: LibConfig[]): LibConfig[] => {
  const seen = new Map<string, number>()
  return configs.map((config, index) => {
    const existingId = toSafeToken((config as any)?.instanceId)
    if (existingId) return config

    const baseFromAgent = toSafeToken((config as any)?.agentId)
    const baseFromName = toSafeToken((config as any)?.name)
    const base = baseFromAgent || baseFromName || `agent-${index + 1}`
    const nextCount = (seen.get(base) || 0) + 1
    seen.set(base, nextCount)
    const instanceId = nextCount > 1 ? `${base}-${nextCount}` : base
    return {
      ...(config as any),
      instanceId,
    } as LibConfig
  })
}

export async function init(config: LibConfig | LibConfig[]) {
  if (!isBrowser) return
  ensureBootstrapIcons()
  if (Array.isArray(config)) {
    const normalized = withAutoInstanceIds(config)
    for (const item of normalized) {
      await initImpl(item)
    }
    return
  }
  await initImpl(config)
}

export async function initShareButtons() {
  if (!isBrowser) return
  ensureBootstrapIcons()
  await initShareButtonsImpl()
}

export async function destroy(instanceId?: string) {
  if (!isBrowser) return
  await destroyImpl?.(instanceId)
}

export async function update(patch: Partial<LibConfig>) {
  if (!isBrowser) return
  ensureBootstrapIcons()
  await updateImpl?.(patch)
}

export default { init, initShareButtons, destroy, update }
